import { useState } from 'react';
import { Search, Users } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Checkbox, Select, Textarea } from '@/components/ui/misc';
import { FormError } from '@/features/auth/auth-layout';
import { useSession } from '@/features/auth/use-session';
import { useUserOptions } from '@/hooks/use-user-options';
import { ApiRequestError } from '@/lib/api';
import { formatDate } from '@/lib/format';
import { useSaveTask } from './api';
import { TASK_PRIORITY_LABELS, TASK_TYPE_LABELS } from './labels';
import type { Task, TaskPriority, TaskType } from './types';

const TYPES = Object.keys(TASK_TYPE_LABELS) as TaskType[];
const PRIORITIES = Object.keys(TASK_PRIORITY_LABELS) as TaskPriority[];

/** "2026-09-12" and "14:30" from an ISO instant, in the reader's own zone. */
function splitDue(iso: string | null): { date: string; time: string } {
  if (!iso) return { date: '', time: '' };
  const at = new Date(iso);
  const pad = (value: number) => String(value).padStart(2, '0');
  return {
    date: `${at.getFullYear()}-${pad(at.getMonth() + 1)}-${pad(at.getDate())}`,
    time: `${pad(at.getHours())}:${pad(at.getMinutes())}`,
  };
}

/**
 * Creating and editing a task.
 *
 * One person is responsible — the assignee — and any number of colleagues can
 * be brought along: they are told when it is set, and it appears on their own
 * day beside their own work. That is the difference between "do this" and
 * "you should know about this", and a CRM needs both.
 */
export function TaskDialog({
  task,
  defaultDate,
  entity,
  onClose,
}: {
  task?: Task;
  /** "2026-09-12" — the day the calendar was clicked on. */
  defaultDate?: string;
  /** Links the task to the record it came from. */
  entity?: { entityType: string; entityId: string };
  onClose: () => void;
}) {
  const save = useSaveTask();
  const { data: me } = useSession();
  const { data: users = [] } = useUserOptions();
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');

  const existing = splitDue(task?.dueAt ?? null);
  const [form, setForm] = useState({
    title: task?.title ?? '',
    description: task?.description ?? '',
    type: task?.type ?? ('TODO' as TaskType),
    priority: task?.priority ?? ('MEDIUM' as TaskPriority),
    assigneeId: task?.assigneeId ?? '',
    date: existing.date || defaultDate || '',
    time: existing.time || '09:00',
    participantIds: task?.participantIds ?? [],
  });

  const set = (patch: Partial<typeof form>) => setForm((current) => ({ ...current, ...patch }));

  const toggleParticipant = (id: string) =>
    set({
      participantIds: form.participantIds.includes(id)
        ? form.participantIds.filter((candidate) => candidate !== id)
        : [...form.participantIds, id],
    });

  const others = users.filter((user) => user.id !== (form.assigneeId || me?.id));
  const shown = search
    ? others.filter((user) => user.name.toLowerCase().includes(search.toLowerCase()))
    : others;

  const submit = async () => {
    setError(null);
    try {
      await save.mutateAsync({
        ...(task ? { id: task.id } : {}),
        title: form.title.trim(),
        description: form.description.trim(),
        type: form.type,
        priority: form.priority,
        assigneeId: form.assigneeId,
        participantIds: form.participantIds,
        // A date with no time means the start of the working day, not midnight.
        dueAt: form.date ? new Date(`${form.date}T${form.time || '09:00'}`).toISOString() : '',
        ...(entity ?? {}),
      });
      onClose();
    } catch (caught) {
      setError(caught instanceof ApiRequestError ? caught.message : 'Could not save the task.');
    }
  };

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>{task ? 'Edit task' : 'New task'}</DialogTitle>
          {form.date && (
            <DialogDescription>
              Due {formatDate(`${form.date}T00:00:00`)}
              {form.time ? ` at ${form.time}` : ''}
            </DialogDescription>
          )}
        </DialogHeader>

        <div className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-1.5 sm:col-span-2">
            <Label htmlFor="task-title">What needs doing</Label>
            <Input
              id="task-title"
              value={form.title}
              autoFocus
              placeholder="Call Ahmed about the renewal"
              onChange={(event) => set({ title: event.target.value })}
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="task-date">Date</Label>
            <Input
              id="task-date"
              type="date"
              value={form.date}
              onChange={(event) => set({ date: event.target.value })}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="task-time">Time</Label>
            <Input
              id="task-time"
              type="time"
              value={form.time}
              onChange={(event) => set({ time: event.target.value })}
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="task-type">Type</Label>
            <Select
              id="task-type"
              value={form.type}
              onChange={(event) => set({ type: event.target.value as TaskType })}
            >
              {TYPES.map((value) => (
                <option key={value} value={value}>
                  {TASK_TYPE_LABELS[value]}
                </option>
              ))}
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="task-priority">Priority</Label>
            <Select
              id="task-priority"
              value={form.priority}
              onChange={(event) => set({ priority: event.target.value as TaskPriority })}
            >
              {PRIORITIES.map((value) => (
                <option key={value} value={value}>
                  {TASK_PRIORITY_LABELS[value]}
                </option>
              ))}
            </Select>
          </div>

          <div className="space-y-1.5 sm:col-span-2">
            <Label htmlFor="task-assignee">Responsible</Label>
            <Select
              id="task-assignee"
              value={form.assigneeId}
              onChange={(event) => set({ assigneeId: event.target.value })}
            >
              <option value="">Me</option>
              {users.map((user) => (
                <option key={user.id} value={user.id}>
                  {user.name}
                </option>
              ))}
            </Select>
          </div>

          <div className="space-y-1.5 sm:col-span-2">
            <Label>Description</Label>
            <Textarea
              rows={2}
              value={form.description}
              placeholder="Anything the person doing it needs to know"
              onChange={(event) => set({ description: event.target.value })}
            />
          </div>
        </div>

        <div className="mt-2 rounded-md border p-3">
          <div className="mb-2 flex flex-wrap items-center gap-2">
            <Users className="h-4 w-4 text-muted-foreground" />
            <span className="text-sm font-medium">Also tell</span>
            <span className="text-xs text-muted-foreground">
              They are notified and see it on their own day.
            </span>
            {form.participantIds.length > 0 && (
              <Badge variant="secondary" className="ms-auto text-[10px]">
                {form.participantIds.length} selected
              </Badge>
            )}
          </div>

          {others.length > 6 && (
            <div className="relative mb-2">
              <Search className="absolute start-2.5 top-2.5 h-3.5 w-3.5 text-muted-foreground" />
              <Input
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder="Find a colleague…"
                className="h-8 ps-8"
              />
            </div>
          )}

          <div className="scrollbar-thin grid max-h-40 gap-x-4 gap-y-1.5 overflow-y-auto sm:grid-cols-2">
            {shown.map((user) => (
              <label
                key={user.id}
                className="flex cursor-pointer items-center gap-2 rounded px-1 py-0.5 text-sm hover:bg-accent"
              >
                <Checkbox
                  checked={form.participantIds.includes(user.id)}
                  onCheckedChange={() => toggleParticipant(user.id)}
                />
                <span className="min-w-0 truncate">{user.name}</span>
                {user.designation && (
                  <span className="truncate text-xs text-muted-foreground">{user.designation}</span>
                )}
              </label>
            ))}
            {shown.length === 0 && (
              <p className="py-2 text-xs text-muted-foreground">Nobody else to add.</p>
            )}
          </div>
        </div>

        <FormError message={error} />

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={submit} disabled={save.isPending || form.title.trim().length < 2}>
            {save.isPending ? 'Saving…' : task ? 'Save changes' : 'Add task'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
